// CTV-653: SDK-free home for the Waymark context types + hooks. Split out of
// WaymarkProvider.tsx so consumers (WaymarkPanel, WaymarkDisclosure, the swap
// tab's commit bridge) can read Waymark state without pulling
// @waymark/waymark-sdk into their import graph — the provider itself stays
// lazy-loadable from EditCampaignModal.

import React from 'react';
import type { WaymarkStatus } from './types';

export interface ProviderState {
  status: WaymarkStatus;
  sourceUrl: string | null;
  videoId: string | null;
  renderedUrl: string | null;
  errorMessage: string | null;
  // CTV-643: the draft's Waymark account lives under a partner the publisher no
  // longer has configured (their agreement was cleared/repointed), so we can't
  // mint a login token and the editor can't be reopened. Distinct from a
  // generation failure — this one is not retryable against Waymark, so the UI
  // steers the buyer to upload a file instead.
  partnerUnavailable: boolean;
}

/**
 * CTV-775 ledger: the coarse buyer-facing stage of the active commercial.
 * Mirrors the disclosure's wait/action states; failed and idle carry no stage.
 */
export type WaymarkStage = 'analyzing' | 'drafting' | 'customize' | 'finalizing' | 'ready';

export interface WaymarkContextValue {
  /** CTV-775: the AI commercial this provider is mounted on (null before one exists). */
  commercialId: string | null;
  status: WaymarkStatus;
  sourceUrl: string | null;
  videoId: string | null;
  renderedUrl: string | null;
  errorMessage: string | null;
  /**
   * True between `videoCompleted` (user clicked Finish in the editor) and
   * `videoRendered` (broadcast-quality file is ready). Lets the UI show a
   * "finalizing your video" indicator during the ~1–2 minute render gap.
   */
  isFinalizing: boolean;
  /** True until the persisted state has been hydrated from the server. */
  isHydrating: boolean;
  /**
   * CTV-643: this draft's Waymark account is under a partner that's no longer
   * configured (the publisher's agreement was cleared/repointed), so its editor
   * can't be reopened. The panel shows a "can't be reopened, upload instead"
   * dead-end when true.
   */
  partnerUnavailable: boolean;
  /**
   * Kick off (or restart) generation for a given website URL.
   * No-op if the URL matches the in-flight `sourceUrl` and status isn't
   * a terminal-failure. The browser persists progress to the server so
   * a refresh resumes the same run.
   */
  startGenerationForUrl: (url: string) => void;
  /**
   * CTV-775 Start over: discard the current video and generate again on the
   * same AI commercial (same row, same URL). No-op when no URL is known.
   */
  restartGeneration: () => void;
  /**
   * Open the Waymark editor on the rendered video so the user can fine-tune
   * it. Returns a promise that resolves when the editor UI is up.
   */
  openEditor: () => Promise<void>;
  /** Hide the editor iframe again (returns the user to the wizard step). */
  closeEditor: () => Promise<void>;
  /** True when the editor iframe is mounted and visible. */
  isEditorOpen: boolean;
  /**
   * CTV-775 ledger: wall-clock (epoch ms) of each stage entry OBSERVED in
   * this session. Transitions that happened before hydration are absent, so
   * the ledger shows a checked row without inventing a time for work done in
   * a previous session. Reset on every fresh generation run and on in-place
   * commercial switches.
   */
  stageEnteredAt: Partial<Record<WaymarkStage, number>>;
}

// Exported (not just the hooks) so tests can wrap components in a fake
// <WaymarkContext.Provider> without mounting the real SDK-backed provider.
export const WaymarkContext = React.createContext<WaymarkContextValue | null>(null);

export function useWaymark(): WaymarkContextValue {
  const ctx = React.useContext(WaymarkContext);
  if (!ctx) throw new Error('useWaymark must be used inside a <WaymarkProvider>');
  return ctx;
}

/**
 * CTV-653: nullable variant for surfaces where the provider may not be
 * mounted (the swap tab before its session resolves, or when the
 * swap-session fetch failed). Consumers no-op on null.
 */
export function useWaymarkOptional(): WaymarkContextValue | null {
  return React.useContext(WaymarkContext);
}

/**
 * Hydration input from the parent (the AI commercial's `initial`). Includes the
 * persisted ProviderState fields plus the `topicId` / `generationRequestId` we
 * need to resume in-flight SDK subscriptions after a refresh. `partnerUnavailable`
 * is excluded: it's a runtime-only flag re-derived at token time, never persisted.
 */
export interface WaymarkInitial extends Omit<ProviderState, 'partnerUnavailable'> {
  topicId?: string | null;
  generationRequestId?: string | null;
}
